import React from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { TranslationProgressEvent } from '../services/translation';

export const useTranslationProgress = () => {
  const [showProgress, setShowProgress] = React.useState(false);
  const [progressInfo, setProgressInfo] = React.useState<{
    current: number;
    total: number;
    message: string;
  }>({ current: 0, total: 0, message: '' });

  const onProgress = (event: TranslationProgressEvent) => {
    setProgressInfo({
      current: event.current,
      total: event.total,
      message: event.message
    });

    if (event.status === 'completed') {
      // Brief delay so user can see "completed" before dialog closes
      setTimeout(() => setShowProgress(false), 500);
    }
  };

  const startProgress = () => {
    setProgressInfo({
      current: 0,
      total: 0,
      message: 'Preparing translation...'
    });
    setShowProgress(true);
  };

  const stopProgress = () => {
    setShowProgress(false);
  };

  const renderTranslationProgressDialog = () => {
    if (!showProgress) return null;

    const percentage =
      progressInfo.total > 0
        ? (progressInfo.current / progressInfo.total) * 100
        : 0;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <Dialog
          title={
            <span className="fw-bold text-primary">
              Translating Resources...
            </span>
          }
          width={450}>
          <div className="p-3 text-center">
            <div
              className="spinner-border text-primary mb-3"
              role="status"
              style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Translating...</span>
            </div>
            <p className="fw-semibold text-dark mb-2">{progressInfo.message}</p>

            {progressInfo.total > 0 && (
              <div className="w-100 mt-3">
                <div
                  className="progress mb-2"
                  style={{ height: '12px', borderRadius: '6px' }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                    role="progressbar"
                    style={{ width: `${percentage}%` }}
                    aria-valuenow={progressInfo.current}
                    aria-valuemin={0}
                    aria-valuemax={progressInfo.total}
                  />
                </div>
                <small className="text-muted fw-medium">
                  Translated {progressInfo.current} of {progressInfo.total}{' '}
                  batches
                </small>
              </div>
            )}
          </div>
        </Dialog>
      </div>
    );
  };

  return {
    onProgress,
    startProgress,
    stopProgress,
    renderTranslationProgressDialog
  };
};
